import * as React from 'react';

import { Col } from '../..';
import { cn } from '../../../../lib/stylingUtils';

/**
 * Props for the Card component.
 */
export interface CardProps extends React.PropsWithChildren {
  /**
   * Additional CSS class names to apply to the card
   */
  className?: string;

  /**
   * Function to call when the card is clicked.
   * When provided, the card becomes interactive (adds cursor-pointer styling)
   * and appropriate accessibility attributes are added.
   */
  onClick?: () => void;

  /**
   * Whether the card should appear in a selected state.
   * Adds a colored border and ring when true.
   * @default false
   */
  isSelected?: boolean;

  /**
   * Whether the card should show hover effects.
   * Adds a subtle shadow effect on hover when true.
   * @default false
   */
  isHoverable?: boolean;

  title?: React.ReactNode;
  titleClassName?: string;
}

/**
 * Card component for displaying content in a contained box.
 *
 * Provides a consistent container for content with options for selection state,
 * hover effects, and click interactions.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Card>
 *   <h3>Card Title</h3>
 *   <p>Card content goes here</p>
 * </Card>
 *
 * // Interactive card
 * <Card onClick={() => handleCardClick(id)} isHoverable>
 *   <h3>Interactive Card</h3>
 *   <p>Click me!</p>
 * </Card>
 *
 * // Selected card
 * <Card isSelected={isItemSelected}>
 *   <h3>Selected Card</h3>
 *   <p>This card is selected</p>
 * </Card>
 * ```
 *
 * @param props - Card properties
 * @returns Rendered Card component
 */
export const Card = ({
  className,
  children,
  onClick,
  isSelected = false,
  isHoverable = false,
  title,
  titleClassName,
}: CardProps) => (
  <Col
    gap4
    className={cn(
      '@Card',
      'rounded-lg border border-foreground/20 bg-space p-3',
      isSelected && 'border-primary/80 ring-1 ring-primary/80',
      isHoverable && 'transition-shadow duration-200 hover:shadow',
      !!onClick && 'cursor-pointer',
      className,
    )}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
  >
    {title && (
      <div className={cn('text-base font-semibold capitalize', titleClassName)}>
        {title}
      </div>
    )}
    {children}
  </Col>
);
